import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@shared/utils";
import { useMutation } from "@apollo/client/react";
import { useAuth } from "@providers/auth";
import {
  PROJECT_MESSAGES_QUERY,
  PROJECT_UNREAD_COUNT_QUERY,
  USERS_QUERY,
  SEND_PROJECT_MESSAGE_MUTATION,
  EDIT_PROJECT_MESSAGE_MUTATION,
  DELETE_PROJECT_MESSAGE_MUTATION,
  TOGGLE_MESSAGE_REACTION_MUTATION,
  MARK_PROJECT_READ_MUTATION,
  GET_PROJECT_FILE_UPLOAD_URL_MUTATION,
} from "./graphql";
import type {
  ProjectMessage,
  User,
  PendingAttachment,
  ProjectFileUpload,
} from "./types";

// Max image dimension for resize
const MAX_IMAGE_DIMENSION = 1920;
const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

// Client-side image resize using canvas
async function resizeImageIfNeeded(file: File): Promise<File> {
  // Only resize images
  if (!file.type.startsWith("image/") || file.type === "image/gif") {
    return file;
  }

  // Skip if already small enough
  if (file.size <= MAX_IMAGE_SIZE_BYTES) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);

      // Calculate new dimensions
      let { width, height } = img;
      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        if (width > height) {
          height = (height / width) * MAX_IMAGE_DIMENSION;
          width = MAX_IMAGE_DIMENSION;
        } else {
          width = (width / height) * MAX_IMAGE_DIMENSION;
          height = MAX_IMAGE_DIMENSION;
        }
      }

      // Draw to canvas
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file); // Fallback to original
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          } else {
            resolve(file);
          }
        },
        "image/jpeg",
        0.8
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image"));
    };
    img.src = URL.createObjectURL(file);
  });
}

export interface UseProjectChatResult {
  messages: ProjectMessage[];
  loading: boolean;
  error?: Error;
  unreadCount: number;
  users: Map<string, User>;
  currentUserId: string | null;
  replyingTo: ProjectMessage | null;
  setReplyingTo: (message: ProjectMessage | null) => void;
  editingMessage: ProjectMessage | null;
  setEditingMessage: (message: ProjectMessage | null) => void;
  pendingAttachments: PendingAttachment[];
  sendMessage: (content: string) => Promise<void>;
  editMessage: (messageId: string, content: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  toggleReaction: (messageId: string, emoji: string) => Promise<void>;
  markAsRead: () => Promise<void>;
  addAttachment: (file: File) => void;
  removeAttachment: (index: number) => void;
  loadMore: () => void;
  hasMore: boolean;
  refetch: () => void;
}

export function useProjectChat(projectId: string): UseProjectChatResult {
  const { user } = useAuth();
  const appMeta = user?.["https://core-backend.fence-estimator/app_metadata"] as
    | { user_id?: string }
    | undefined;
  const currentUserId = appMeta?.user_id || user?.sub || null;

  const [replyingTo, setReplyingTo] = useState<ProjectMessage | null>(null);
  const [editingMessage, setEditingMessage] = useState<ProjectMessage | null>(
    null
  );
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const [hasMore, setHasMore] = useState(true);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch messages
  const {
    data: messagesData,
    loading: messagesLoading,
    error: messagesError,
    refetch: refetchMessages,
    fetchMore,
  } = useQuery<{
    projectMessages: ProjectMessage[];
  }>(PROJECT_MESSAGES_QUERY, {
    variables: { projectId, limit: 50 },
    skip: !projectId,
    fetchPolicy: "cache-and-network",
  });

  // Fetch unread count
  const { data: unreadData, refetch: refetchUnread } = useQuery<{
    projectUnreadCount: number;
  }>(PROJECT_UNREAD_COUNT_QUERY, {
    variables: { projectId },
    skip: !projectId,
    fetchPolicy: "cache-and-network",
  });

  // Fetch users for name resolution
  const { data: usersData } = useQuery<{
    users: User[];
  }>(USERS_QUERY);

  // Build user map - keyed by auth0_id since that's what userId in messages contains
  const users = useMemo(() => {
    const map = new Map<string, User>();
    usersData?.users?.forEach((u) => {
      // Map by auth0_id (which matches the userId stored in messages)
      if (u.auth0_id) {
        map.set(u.auth0_id, u);
      }
      // Also map by _id as fallback
      map.set(u._id, u);
    });
    return map;
  }, [usersData]);

  // Mutations
  const [sendMessageMutation] = useMutation(SEND_PROJECT_MESSAGE_MUTATION);
  const [editMessageMutation] = useMutation(EDIT_PROJECT_MESSAGE_MUTATION);
  const [deleteMessageMutation] = useMutation(DELETE_PROJECT_MESSAGE_MUTATION);
  const [toggleReactionMutation] = useMutation(TOGGLE_MESSAGE_REACTION_MUTATION);
  const [markReadMutation] = useMutation(MARK_PROJECT_READ_MUTATION);
  const [getUploadUrlMutation] = useMutation<{
    getProjectFileUploadUrl: ProjectFileUpload;
  }>(GET_PROJECT_FILE_UPLOAD_URL_MUTATION);

  // Poll for new messages every 30 seconds
  useEffect(() => {
    if (!projectId) return;

    pollIntervalRef.current = setInterval(() => {
      refetchMessages();
      refetchUnread();
    }, 30000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [projectId, refetchMessages, refetchUnread]);

  // Upload a file and return the key
  const uploadFile = useCallback(
    async (file: File): Promise<string> => {
      // Resize images if needed
      const processedFile = await resizeImageIfNeeded(file);
      
      // Use the processed file's content type (important: resize changes type to jpeg)
      const contentType = processedFile.type;

      // Get presigned URL with the ACTUAL content type we'll upload
      const { data } = await getUploadUrlMutation({
        variables: {
          input: {
            projectId,
            filename: processedFile.name,
            contentType: contentType,
          },
        },
      });

      if (!data?.getProjectFileUploadUrl) {
        throw new Error("Failed to get upload URL");
      }

      const { uploadUrl, key } = data.getProjectFileUploadUrl;

      // Upload to S3
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: processedFile,
        headers: {
          "Content-Type": contentType,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Upload failed:", response.status, response.statusText, errorText);
        throw new Error(`Failed to upload file: ${response.status}`);
      }

      console.log("File uploaded successfully, key:", key);
      return key;
    },
    [projectId, getUploadUrlMutation]
  );

  // Add attachment
  const addAttachment = useCallback((file: File) => {
    const preview = file.type.startsWith("image/")
      ? URL.createObjectURL(file)
      : undefined;

    setPendingAttachments((prev) => [...prev, { file, preview }]);
  }, []);

  // Remove attachment
  const removeAttachment = useCallback((index: number) => {
    setPendingAttachments((prev) => {
      const attachment = prev[index];
      if (attachment?.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Send message
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() && pendingAttachments.length === 0) return;

      // Upload all attachments first
      const attachmentKeys: string[] = [];
      for (let i = 0; i < pendingAttachments.length; i++) {
        const attachment = pendingAttachments[i];
        try {
          setPendingAttachments((prev) =>
            prev.map((a, idx) =>
              idx === i ? { ...a, uploading: true, error: undefined } : a
            )
          );
          const key = await uploadFile(attachment.file);
          attachmentKeys.push(key);
          setPendingAttachments((prev) =>
            prev.map((a, idx) =>
              idx === i ? { ...a, uploading: false, key } : a
            )
          );
        } catch (err) {
          setPendingAttachments((prev) =>
            prev.map((a, idx) =>
              idx === i
                ? { ...a, uploading: false, error: "Upload failed" }
                : a
            )
          );
          throw err;
        }
      }

      await sendMessageMutation({
        variables: {
          input: {
            projectId,
            content: content.trim(),
            attachments: attachmentKeys.length > 0 
              ? attachmentKeys.map((key, idx) => ({
                  key,
                  filename: pendingAttachments[idx]?.file.name || 'file',
                  contentType: pendingAttachments[idx]?.file.type || 'application/octet-stream',
                  size: pendingAttachments[idx]?.file.size || 0,
                }))
              : undefined,
            replyToId: replyingTo?._id,
          },
        },
      });
      
      // Refetch to get signed URLs for attachments
      refetchMessages();

      // Clear state
      setReplyingTo(null);
      setPendingAttachments([]);
    },
    [
      projectId,
      pendingAttachments,
      replyingTo,
      sendMessageMutation,
      uploadFile,
    ]
  );

  // Edit message
  const editMessage = useCallback(
    async (messageId: string, content: string) => {
      await editMessageMutation({
        variables: { input: { messageId, content } },
      });
      setEditingMessage(null);
      refetchMessages();
    },
    [editMessageMutation, refetchMessages]
  );

  // Delete message
  const deleteMessage = useCallback(
    async (messageId: string) => {
      await deleteMessageMutation({
        variables: { input: { messageId } },
      });
      refetchMessages();
    },
    [deleteMessageMutation, refetchMessages]
  );

  // Toggle reaction
  const toggleReaction = useCallback(
    async (messageId: string, emoji: string) => {
      await toggleReactionMutation({
        variables: { input: { messageId, emoji } },
      });
      refetchMessages();
    },
    [toggleReactionMutation, refetchMessages]
  );

  // Mark as read
  const markAsRead = useCallback(async () => {
    if (!projectId) return;
    await markReadMutation({
      variables: { input: { projectId } },
    });
    refetchUnread();
  }, [projectId, markReadMutation, refetchUnread]);

  // Load more messages
  const loadMore = useCallback(async () => {
    const messages = messagesData?.projectMessages;
    if (!messages || messages.length === 0) return;

    const oldestMessage = messages[0];
    const { data } = await fetchMore({
      variables: {
        projectId,
        limit: 50,
        before: oldestMessage._id,
      },
    });

    if ((data?.projectMessages?.length ?? 0) < 50) {
      setHasMore(false);
    }
  }, [messagesData, fetchMore, projectId]);

  const refetch = useCallback(() => {
    refetchMessages();
    refetchUnread();
  }, [refetchMessages, refetchUnread]);

  return {
    messages: messagesData?.projectMessages || [],
    loading: messagesLoading,
    error: messagesError,
    unreadCount: unreadData?.projectUnreadCount || 0,
    users,
    currentUserId,
    replyingTo,
    setReplyingTo,
    editingMessage,
    setEditingMessage,
    pendingAttachments,
    sendMessage,
    editMessage,
    deleteMessage,
    toggleReaction,
    markAsRead,
    addAttachment,
    removeAttachment,
    loadMore,
    hasMore,
    refetch,
  };
}
